/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Invite Child Modal Component
 * Modal for inviting children to family organization
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState } from 'react'
import { X, Mail, User, Copy, Check, AlertCircle, Loader2 } from 'lucide-react'
import api from '../services/api'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('InviteChildModal')

const InviteChildModal = ({ isOpen, onClose, onInviteCreated, seatsAvailable }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [invitationLink, setInvitationLink] = useState(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)

  if (!isOpen) return null

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (!formData.email.trim()) {
      setError('Email is required')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address')
      return
    }

    if (seatsAvailable <= 0) {
      setError('No available seats. Please upgrade your plan.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await api.post('/family/invite', {
        email: formData.email.toLowerCase(),
        role: 'student',
        firstName: formData.firstName.trim() || undefined,
        lastName: formData.lastName.trim() || undefined
      })

      if (response.data.success) {
        const token = response.data.invitation.token || response.data.invitation.invitationToken
        const appUrl = window.location.origin
        const link = `${appUrl}/register/student?token=${token}`

        setInvitationLink(link)
        toast.success('Invitation created successfully!')

        // Add the full invitation data with the token field for display
        const fullInvitation = {
          ...response.data.invitation,
          invitationToken: token, // Ensure invitationToken field exists for PendingInvitationsPanel
          createdAt: new Date().toISOString()
        }

        if (onInviteCreated) {
          onInviteCreated(fullInvitation)
        }
      }
    } catch (error) {
      logger.error('Error creating invitation:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to create invitation'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyLink = async () => {
    if (!invitationLink) return

    try {
      await navigator.clipboard.writeText(invitationLink)
      setCopied(true)
      toast.success('Link copied to clipboard!')

      setTimeout(() => {
        setCopied(false)
      }, 2000)
    } catch (error) {
      logger.error('Failed to copy:', error)
      toast.error('Failed to copy link')
    }
  }

  const handleClose = () => {
    setFormData({ email: '', firstName: '', lastName: '' })
    setInvitationLink(null)
    setCopied(false)
    setError(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {invitationLink ? 'Invitation Created' : 'Add Child to Family'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {invitationLink ? (
            // Success state - show invitation link
            <div className="space-y-4">
              <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-success-900 dark:text-success-100">
                      Invitation created successfully!
                    </p>
                    <p className="text-xs text-success-700 dark:text-success-300 mt-1">
                      Share this link with {formData.email}. It will expire in 72 hours.
                    </p>
                  </div>
                </div>
              </div>

              {/* Invitation Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Invitation Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={invitationLink}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button onClick={handleClose} className="flex-1 btn-primary">
                  Done
                </button>
              </div>
            </div>
          ) : (
            // Form state - collect email and optional name
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Seats info */}
              {seatsAvailable !== null && (
                <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
                  <p className="text-sm text-primary-900 dark:text-primary-100">
                    <strong>{seatsAvailable}</strong> seat{seatsAvailable !== 1 ? 's' : ''}{' '}
                    available
                  </p>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-error-600 dark:text-error-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error-900 dark:text-error-100">{error}</p>
                  </div>
                </div>
              )}

              {/* Email input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Child's Email Address *
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Mail className="w-5 h-5" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="child@example.com"
                    required
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  />
                </div>
              </div>

              {/* First name input (optional) */}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  First Name <span className="text-gray-400">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <User className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  />
                </div>
              </div>

              {/* Last name input (optional) */}
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Last Name <span className="text-gray-400">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <User className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  />
                </div>
              </div>

              {/* Info text */}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                An invitation link will be generated that you can share with the child. The link
                expires in 72 hours.
              </p>

              {/* Action buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || seatsAvailable <= 0}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </span>
                  ) : (
                    'Create Invitation'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default InviteChildModal
